import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { IoPersonCircle } from "react-icons/io5";
import { FaRegMoon } from "react-icons/fa";
import { MdCategory } from "react-icons/md";
import { TbLanguage, TbWorld } from "react-icons/tb";
import { LANGUAGES } from "../../data/DemoData";
import { Link } from "react-router-dom";

import { Person, ShoppingBagIcon, SearchIcon } from "../../assets/icons";
import DataUpLogo from "../../assets/images/DataUpLogo.svg";
import Page from "../../assets/images/Page.svg";
import { Button } from "../Button";
import { useDarkMode } from "../../context/DarkModeContext";
import { useAppSelector } from "../../hooks/hooks";
import { useModal } from "../../context/ModalContext";
import { CategoriesDropdown } from "./Category";
import { LocaleDropdown } from "./Localisation";
import { WalletDropdown } from "./Wallet";
import ProfileMenu from "./ProfileMenu";
import useClickOutside from "../../hooks/useClickOutside";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../hooks/hooks";
import { fetchSearchResults, clearSearch } from "../../features/store/searchSlice";
import SearchSuggestions from "../SearchSuggestion";
import {
  fetchUserCountry,
  setSelectedCountry,
} from "../../features/store/localeSlice";
import { fetchAppData } from "../../features/store/appDataSlice";
import { formatCurrency } from "../../utils/utils";
import {
  Country,
  CountryState,
  LocaleState,
} from "../../features/store/types/locale-types";

const Navbar: React.FC = () => {
  const { openLoginModal, openSignupModal } = useModal();
  const [isLocalisationOpen, setIsLocalisationOpen] = useState(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const isAuthenticated = !!user;
  const categoriesBtnRef = useRef<HTMLDivElement>(null);
  const localisationBtnRef = useRef<HTMLDivElement>(null);
  const walletBtnRef = useRef<HTMLDivElement>(null);
  const profileBtnRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  useInitLocale();

  // Memoized toggles
  const toggleLocalisationDropdown = useCallback(
    () => setIsLocalisationOpen((v) => !v),
    [],
  );
  const toggleCategoriesDropdown = useCallback(
    () => setIsCategoriesOpen((v) => !v),
    [],
  );
  const toggleMenuProfile = useCallback(() => setIsMenuOpen((v) => !v), []);

  return (
    <div className="sticky top-0 left-0 right-0 z-50 font-satoshi px-[16px] bp:px-[0px] h-28 bp:h-[80px] flex flex-col md:flex-row items-center justify-between w-full">
      <div className="hidden lg:block">
        <DesktopNavbar
          toggleLocalisationDropdown={toggleLocalisationDropdown}
          isLocalisationOpen={isLocalisationOpen}
          setIsLocalisationOpen={setIsLocalisationOpen}
          walletBtnRef={walletBtnRef}
          categoriesBtnRef={categoriesBtnRef}
          profileBtnRef={profileBtnRef}
          localisationBtnRef={localisationBtnRef}
          searchRef={searchRef}
          toggleCategoriesDropdown={toggleCategoriesDropdown}
          isCategoriesOpen={isCategoriesOpen}
          setIsCategoriesOpen={setIsCategoriesOpen}
          toggleMenuProfile={toggleMenuProfile}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isAuthenticated={isAuthenticated}
          openLoginModal={openLoginModal}
          openSignupModal={openSignupModal}
          user={user}
        />
      </div>
      <div className="block lg:hidden">
        <MobileNavbar
          toggleLocalisationDropdown={toggleLocalisationDropdown}
          isLocalisationOpen={isLocalisationOpen}
          setIsLocalisationOpen={setIsLocalisationOpen}
          toggleCategoriesDropdown={toggleCategoriesDropdown}
          isCategoriesOpen={isCategoriesOpen}
          setIsCategoriesOpen={setIsCategoriesOpen}
          toggleMenuProfile={toggleMenuProfile}
          categoriesBtnRef={categoriesBtnRef}
          profileBtnRef={profileBtnRef}
          localisationBtnRef={localisationBtnRef}
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          isAuthenticated={isAuthenticated}
          openLoginModal={openLoginModal}
          user={user}
        />
      </div>
    </div>
  );
};

const MobileNavbar = ({
  toggleLocalisationDropdown,
  isLocalisationOpen,
  setIsLocalisationOpen,
  toggleCategoriesDropdown,
  isCategoriesOpen,
  setIsCategoriesOpen,
  toggleMenuProfile,
  categoriesBtnRef,
  localisationBtnRef,
  profileBtnRef,
  isMenuOpen,
  setIsMenuOpen,
  isAuthenticated,
  openLoginModal,
  user,
}: any) => {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const localeState = useAppSelector((state) => state.locale);
  const { i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0],
  );

  return (
    <div className="w-screen bg-white dark:bg-black dark:border-gray-700">
      <div className="flex items-center gap-10 justify-between px-2 py-2">
        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <div ref={profileBtnRef} onClick={toggleMenuProfile}>
              <IconWrapper>
                <img
                  src={user?.image_url || "/png/default.png"}
                  alt="Profile"
                  style={{ width: 32, height: 32 }}
                  className="size-28 sm:size-40 rounded-full bg-profPixBG aspect-square object-cover"
                />
              </IconWrapper>
            </div>
          ) : (
            <div onClick={openLoginModal}>
              <Button
                className="text-[14px] md:p-2 lg:p-2 lg:text-xs xl:text-base"
                bgColor="bg-[#F4F4F5] dark:bg-[#FFFFFF1A]"
              >
                Sign In
              </Button>
            </div>
          )}
          <div ref={categoriesBtnRef} onClick={toggleCategoriesDropdown}>
            <IconWrapper>
              <MdCategory className="w-5 h-5 dark:text-white relative" />
            </IconWrapper>
          </div>
          <Link to="/">
            <img
              src={darkMode ? Page : DataUpLogo}
              alt="Logo"
              className="w-20"
            />
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <div
            ref={localisationBtnRef}
            onMouseDown={(e) => {
              e.stopPropagation();
              toggleLocalisationDropdown();
            }}
            title="Region, Language & Currency"
          >
            <div className="cursor-pointer flex items-center space-x-1.5 transition-colors duration-150 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 bg-gray-100 dark:bg-gray-800 rounded-3xl shadow-sm">
              <IconWrapper>
                <div className="size-[25px] flex items-center justify-center overflow-hidden">
                  {localeState.selectedCountry.image_url ? (
                    <img
                      src={localeState.selectedCountry.image_url}
                      alt={`${localeState.selectedCountry.name} flag`}
                      className="w-full h-full object-cover rounded-sm"
                    />
                  ) : (
                    <TbWorld size={24} className="dark:text-white" />
                  )}
                </div>
              </IconWrapper>
              {/* <span className="text-sm font-medium dark:text-white">
                {selectedLanguage.code}
              </span>
              <span className="text-sm font-medium dark:text-white">
                {localeState.selectedCurrency.symbol}
              </span> */}
            </div>
          </div>
          <div onClick={toggleDarkMode}>
            <IconWrapper>
              <FaRegMoon size={20} className="dark:text-white" />
            </IconWrapper>
          </div>
          {/* <IconWrapper>
            <ShoppingBagIcon theme={darkMode ? "dark" : "light"} />
          </IconWrapper> */}
        </div>
      </div>
      <div className="px-2">
        <Search isMobile={true} />
      </div>
      {isMenuOpen && (
        <div className="absolute top-0 left-4 z-10">
          <ProfileMenu
            profileBtnRef={profileBtnRef}
            user={user}
            setIsOpen={setIsMenuOpen}
          />
        </div>
      )}
      {isCategoriesOpen && (
        <div className="absolute top-16 left-4 z-10">
          <CategoriesDropdown
            categoriesBtnRef={categoriesBtnRef}
            setIsOpen={setIsCategoriesOpen}
          />
        </div>
      )}
      {isLocalisationOpen && (
        <div className="absolute top-16 right-4 z-10">
          <LocaleDropdown
            localisationBtnRef={localisationBtnRef}
            setIsOpen={setIsLocalisationOpen}
          />
        </div>
      )}
    </div>
  );
};

const DesktopNavbar = ({
  toggleLocalisationDropdown,
  isLocalisationOpen,
  setIsLocalisationOpen,
  toggleCategoriesDropdown,
  isCategoriesOpen,
  setIsCategoriesOpen,
  toggleMenuProfile,
  localisationBtnRef,
  categoriesBtnRef,
  profileBtnRef,
  walletBtnRef,
  searchRef,
  isMenuOpen,
  setIsMenuOpen,
  isAuthenticated,
  openLoginModal,
  openSignupModal,
  user,
}: any) => {
  const { darkMode, toggleDarkMode } = useDarkMode();
  return (
    <div className="w-screen font-satoshi bg-white dark:bg-black h-[80px] flex flex-col md:flex-row items-center justify-between px-14 py-4 md:py-4 lg:py-3 gap-2">
      <LogoSection
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        isAuthenticated={isAuthenticated}
        toggleCategoriesDropdown={toggleCategoriesDropdown}
        isCategoriesOpen={isCategoriesOpen}
        categoriesBtnRef={categoriesBtnRef}
        localisationBtnRef={localisationBtnRef}
        toggleLocalisationDropdown={toggleLocalisationDropdown}
      />
      <div className="w-full flex-1">
        <Search searchRef={searchRef} isMobile={false} />
      </div>
      <AuthenticatedSection
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        toggleLocalisationDropdown={toggleLocalisationDropdown}
        localisationBtnRef={localisationBtnRef}
        toggleMenuProfile={toggleMenuProfile}
        profileBtnRef={profileBtnRef}
        walletBtnRef={walletBtnRef}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        openLoginModal={openLoginModal}
        openSignupModal={openSignupModal}
        isAuthenticated={isAuthenticated}
        user={user}
      />
      {isCategoriesOpen && (
        <div className="absolute top-16 left-8">
          <CategoriesDropdown
            categoriesBtnRef={categoriesBtnRef}
            setIsOpen={setIsCategoriesOpen}
          />
        </div>
      )}
      {isLocalisationOpen && (
        <div className="absolute top-16 right-80 z-50">
          <LocaleDropdown
            localisationBtnRef={localisationBtnRef}
            setIsOpen={setIsLocalisationOpen}
          />
        </div>
      )}
    </div>
  );
};

const LogoSection = ({
  toggleDarkMode,
  darkMode,
  isAuthenticated,
  toggleCategoriesDropdown,
  isCategoriesOpen,
  toggleLocalisationDropdown,
  categoriesBtnRef,
  localisationBtnRef,
}: any) => {
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;

  const handleCategoriesToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCategoriesDropdown();
  };

  // const handleLocalisationToggle = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   toggleLocalisationDropdown();
  // };

  return (
    <div className="flex items-center gap-2">
      <div className="flex gap-2">
        <div className="md:hidden">
          <Link to="/signin">
            <IoPersonCircle size="28" />
          </Link>
        </div>
        <Link to="/">
          <img
            src={DataUpLogo}
            alt="dataUpLogo"
            className="w-20 md:w-32 block dark:hidden"
          />
          <img
            src={Page}
            alt="DarkModeLogo"
            className="w-20 md:w-32 hidden dark:block"
          />
        </Link>
      </div>

      {/* Categories button - Desktop */}
      <div ref={categoriesBtnRef} onMouseDown={handleCategoriesToggle}>
        <Button className="text-[10px] bg-[#F4F4F5] md:p-2 lg:p-0 lg:text-xs hidden md:block relative">
          {translate("navbar.categories")}
        </Button>
      </div>

      {/* Mobile controls */}
      <div className="flex items-center gap-4 md:hidden relative">
        <div
          ref={localisationBtnRef}
          onMouseDown={(e) => {
            e.stopPropagation();
            toggleLocalisationDropdown();
          }}
        >
          <IconWrapper>
            <TbWorld />
          </IconWrapper>
        </div>
        <div onClick={toggleDarkMode}>
          <IconWrapper>
            <FaRegMoon size={24} className="dark:text-white" />
          </IconWrapper>
        </div>
      </div>
    </div>
  );
};

const Search = ({
  searchRef,
  isMobile = false,
}: {
  searchRef?: React.RefObject<HTMLDivElement>;
  isMobile?: boolean;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const inputId = isMobile
    ? "navbar-search-input-mobile"
    : "navbar-search-input-desktop";

  // Close suggestions when clicking outside (handler first, then enabled flag, then ref)
  useClickOutside(
    () => {
      setShowSuggestions(false);
    },
    true,
    searchRef,
  );

  // Debounce search - wait for user to stop typing
  useEffect(() => {
    if (inputValue.trim().length === 0) {
      dispatch(clearSearch());
      setShowSuggestions(false);
      return;
    }

    if (inputValue.trim().length < 2) {
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      // Perform search after 300ms of no typing
      dispatch(fetchSearchResults(inputValue.trim()));
      setShowSuggestions(true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, dispatch]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      // Navigate to search results when Enter is pressed
      navigate(`/search?q=${encodeURIComponent(inputValue.trim())}`);
      setShowSuggestions(false);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (inputValue.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleClear = () => {
    setInputValue("");
    dispatch(clearSearch());
    setShowSuggestions(false);
  };

  return (
    <form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        if (inputValue.trim()) {
          navigate(`/search?q=${encodeURIComponent(inputValue.trim())}`);
          setShowSuggestions(false);
        }
      }}
    >
      <div
        ref={searchRef}
        className="relative flex items-center max-w-full lg:max-w-full"
      >
        <div
          className={`absolute left-1/2 -translate-x-1/2 flex items-center gap-2 pointer-events-none transition-opacity duration-200 ${
            inputValue || isFocused ? "opacity-0" : "opacity-100"
          }`}
        >
          <SearchIcon />
          <span className="text-xs md:text-xs lg:text-sm text-black dark:text-white whitespace-nowrap">
            {translate("navbar.search")}
          </span>
        </div>
        <input
          className="w-full h-[40px] bg-[#F4F4F5] dark:bg-[#FFFFFF1A] rounded-full focus:outline-none text-gray-600 dark:text-gray-300 text-center pl-4 pr-10"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onFocus={handleFocus}
          onBlur={() => setIsFocused(false)}
          placeholder={isFocused ? "" : undefined}
          autoComplete="new-password"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          name={`search-query-${isMobile ? "mobile" : "desktop"}`}
          id="navbar-search-input"
          type="text"
          role="searchbox"
          aria-label="Search"
          data-form-type="other"
        />
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            ✕
          </button>
        )}

        <SearchSuggestions
          isVisible={showSuggestions}
          searchQuery={inputValue}
        />
      </div>
    </form>
  );
};

const AuthAndNavSection = ({
  toggleDarkMode,
  darkMode,
  toggleLocalisationDropdown,
  localisationBtnRef,
  isAuthenticated,
  openLoginModal,
  openSignupModal,
}: any) => {
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;

  return (
    <div className="hidden md:flex items-center gap-4">
      <div
        ref={localisationBtnRef}
        onMouseDown={(e) => {
          e.stopPropagation();
          toggleLocalisationDropdown();
        }}
        className="relative"
      >
        <LocaleSection />
      </div>
      <div onClick={toggleDarkMode}>
        <IconWrapper>
          <FaRegMoon size={20} className="dark:text-white" />
        </IconWrapper>
      </div>
      <div className="flex items-center gap-2 lg:gap-4">
        <Button
          className="text-[14px] md:p-2 lg:p-0 lg:text-xs xl:text-base"
          onClick={openLoginModal}
          bgColor="bg-[#F4F4F5] dark:bg-[#FFFFFF1A]"
        >
          {translate("navbar.signin")}
        </Button>
        <Button
          className="text-[14px] md:p-2 lg:p-0 lg:text-xs xl:text-base"
          bgColor="bg-[#039EF2]"
          textColor="text-white"
          onClick={openSignupModal}
        >
          {translate("navbar.signup")}
        </Button>
      </div>
      <Link to="/cart">
        {/* <IconWrapper>
          <ShoppingBagIcon theme={darkMode ? "dark" : "light"} />
        </IconWrapper> */}
      </Link>
    </div>
  );
};

const AuthenticatedSection = ({
  toggleDarkMode,
  darkMode,
  toggleLocalisationDropdown,
  localisationBtnRef,
  toggleMenuProfile,
  profileBtnRef,
  walletBtnRef,
  setIsMenuOpen,
  isMenuOpen,
  isAuthenticated,
  user,
  openLoginModal,
  openSignupModal,
}: any) => {
  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const { t } = useTranslation();
  const translate = t as unknown as (key: string) => string;
  const toggleWalletDropdown = useCallback(
    () => setIsWalletOpen((v) => !v),
    [],
  );

  if (!isAuthenticated) {
    return (
      <AuthAndNavSection
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        toggleLocalisationDropdown={toggleLocalisationDropdown}
        localisationBtnRef={localisationBtnRef}
        isAuthenticated={isAuthenticated}
        openLoginModal={openLoginModal}
        openSignupModal={openSignupModal}
      />
    );
  }

  return (
    <div className="hidden md:flex items-center gap-4">
      <div
        ref={localisationBtnRef}
        onMouseDown={(e) => {
          e.stopPropagation();
          toggleLocalisationDropdown();
        }}
        className="relative"
      >
        <LocaleSection />
      </div>
      <div className="flex items-center gap-2 lg:gap-4">
        <div className="gap-2 text-[10px] bg-[#F4F4F5] dark:bg-[#FFFFFF1A] md:p-2 lg:p-0 lg:text-xs rounded-[24px] w-[110px] h-[40px]">
          <div
            className="flex items-center gap-[12px] py-[6px] px-[14px] relative cursor-pointer"
            onClick={toggleWalletDropdown}
            ref={walletBtnRef}
          >
            <img src="/png/dg.png" alt="Coins" className="w-5 h-5" />
            <p className="text-sm dark:text-white">
              {formatCurrency(user?.dg_coin_wallet_balance)}
            </p>
          </div>
        </div>
        <div
          className="relative flex gap-2 text-[10px] bg-[#F4F4F5] dark:bg-[#FFFFFF1A] md:p-2 lg:p-0 lg:text-xs rounded-[24px] w-[165px] h-[40px]"
          ref={profileBtnRef}
          onClick={toggleMenuProfile}
        >
          <div className="flex items-center gap-[12px] py-[6px] px-[14px] cursor-pointer">
            <img
              src={user?.image_url || "/png/default.png"}
              alt="Profile"
              style={{ width: 32, height: 32 }}
              className="size-28 sm:size-40 rounded-full p-1 bg-profPixBG aspect-square object-cover"
            />
            <p className="text-sm dark:text-white">
              {user?.username ? user.username : user.firstname}
            </p>
          </div>
        </div>
      </div>
      <div onClick={toggleDarkMode}>
        <IconWrapper>
          <FaRegMoon size={20} className="dark:text-white" />
        </IconWrapper>
      </div>
      {/* <Link to="/cart">
        <IconWrapper>
          <ShoppingBagIcon theme={darkMode ? "dark" : "light"} />
        </IconWrapper>
      </Link> */}
      {isWalletOpen && (
        <div className="absolute top-20 right-80 z-10">
          <WalletDropdown
            walletBtnRef={walletBtnRef}
            setIsOpen={setIsWalletOpen}
          />
        </div>
      )}
      {isMenuOpen && (
        <div className="absolute top-10 right-24 z-20">
          <ProfileMenu
            profileBtnRef={profileBtnRef}
            user={user}
            setIsOpen={setIsMenuOpen}
          />
        </div>
      )}
    </div>
  );
};

const useLocaleData = () => {
  return useAppSelector((state) => state.locale);
};

const useInitLocale = () => {
  const dispatch = useAppDispatch();
  const { country, userLocation, selectedCountry } = useLocaleData();
  const { data, loading: appDataLoading } = useAppSelector(
    (state) => state.appData,
  );
  const hadCachedLocaleOnMount = useRef(userLocation.attempted);
  const isUsingDefaultCountry = selectedCountry?.id === 3;
  const [currentCountry, setCurrentCountry] = useState(userLocation.data);

  useEffect(() => {
    if (
      !hadCachedLocaleOnMount.current &&
      userLocation.attempted &&
      !appDataLoading &&
      Object.keys(data).length === 0
    ) {
      dispatch(fetchAppData());
    }
  }, [dispatch, userLocation.attempted, appDataLoading, data]);

  useEffect(() => {
    if (!hadCachedLocaleOnMount.current) return;

    const refreshSilently = async () => {
      await dispatch(fetchUserCountry({ silent: true }));
      dispatch(fetchAppData({ silent: true }));
    };

    refreshSilently();
  }, [dispatch]);

  useEffect(() => {
    if (
      userLocation.data === null &&
      !userLocation.loading &&
      !userLocation.attempted
    ) {
      dispatch(fetchUserCountry());
    }
  }, [
    dispatch,
    userLocation.data,
    userLocation.loading,
    userLocation.attempted,
  ]);

  useEffect(() => {
    const data = userLocation.data;
    const items = country.items;

    if (
      data &&
      items.length > 0 &&
      isUsingDefaultCountry &&
      data.countryCode.toLowerCase() !==
        currentCountry?.countryCode.toLowerCase()
    ) {
      const matchedCountry = items.find(
        (c) => c.symbol.toLowerCase() === data.countryCode.toLowerCase(),
      );

      if (matchedCountry) {
        dispatch(setSelectedCountry(matchedCountry));
        setCurrentCountry(data);
      }
    }
  }, [
    dispatch,
    userLocation.data,
    country.items,
    currentCountry,
    isUsingDefaultCountry,
  ]);
};

const LocaleSection = () => {
  const { country, currency, selectedCountry, selectedCurrency } =
    useLocaleData();
  const { i18n } = useTranslation();

  const selectedLanguage = useMemo(() => {
    return (
      LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0]
    );
  }, [i18n.language]);

  if (currency.loading || country.loading) {
    return (
      <div className="flex justify-center items-center h-16">
        <Spinner className="text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="cursor-pointer flex items-center space-x-1.5 px-[14px] h-[40px] rounded-[24px] transition-colors duration-150 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 bg-gray-100 dark:bg-gray-800 shadow-sm">
      <CountryLocale countryState={country} selectedCountry={selectedCountry} />
      {/* <span className="text-sm font-medium dark:text-white">
        {selectedLanguage.code}
      </span>
      <span className="text-sm font-medium dark:text-white">
        {selectedCurrency.symbol}
      </span> */}
    </div>
  );
};

const Spinner = ({ className = "" }) => (
  <svg
    className={`animate-spin h-5 w-5 ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const IconWrapper = ({
  src,
  alt,
  children,
}: {
  src?: React.ReactNode;
  alt?: string;
  children?: React.ReactNode;
}) => (
  <div className="bg-[#F4F4F5] dark:bg-gray-800 p-2 lg:p-2 rounded-3xl">
    {src ? (
      typeof src === "string" ? (
        <img src={src} alt={alt} />
      ) : (
        src
      )
    ) : (
      children
    )}
  </div>
);

export const CountryLocale = ({
  countryState,
  selectedCountry,
}: {
  countryState: CountryState;
  selectedCountry: Country;
}) => {
  if (countryState.loading) {
    return (
      <div className="flex justify-center items-center h-16">
        <Spinner className="text-indigo-500" />
      </div>
    );
  }

  return (
    <IconWrapper>
      <div className="size-[25px] flex items-center justify-center overflow-hidden">
        {selectedCountry.image_url ? (
          <img
            src={selectedCountry.image_url}
            alt={`${selectedCountry.name} flag`}
            className="w-full h-full object-cover rounded-sm"
          />
        ) : (
          <TbWorld size={24} className="dark:text-white" />
        )}
      </div>
    </IconWrapper>
  );
};

export default Navbar;
